// GSAP is loaded as a WordPress script dependency (registered in assets.php)
import debounce from '../../../assets/js/base/_debounce.js';

// Mobile breakpoint matches $breakpoints sm (48rem = 768px)
const MOBILE_BREAKPOINT = 768;

function initScrollEffects( node, gsap, ScrollTrigger ) {
	// Disable scroll-behavior: smooth set in reset.scss which causes issues with ScrollTrigger
	document.documentElement.style.scrollBehavior = 'auto';

	const introLayout = node.querySelector( '.hero__intro-layout' );
	const entryContent = document.querySelector( '.entry-content' );
	const masthead = document.querySelector( '.masthead' );

	// Store ScrollTrigger instances for cleanup
	let pinTrigger = null;
	let entryPinTrigger = null;
	let fadeAnimation = null;

	// Media query for mobile breakpoint
	const mobileQuery = window.matchMedia( `(max-width: ${ MOBILE_BREAKPOINT }px)` );

	/**
	 * Setup scroll effects (pin and fade)
	 */
	function setupEffects() {
		const getMastheadOffset = () => ( masthead ? Math.round( masthead.getBoundingClientRect().height / 2 ) : 0 );

		// Pin .entry-content briefly when it hits the top of the viewport
		// Only pin if .home-featured > .media-slider is the leading content and fits in the viewport
		const mediaSlider = entryContent?.querySelector( ':scope > .home-featured:first-child > .media-slider:first-child' );
		const mastheadHeight = masthead ? masthead.getBoundingClientRect().height : 0;
		const availableHeight = window.innerHeight - mastheadHeight;
		const shouldPin = mediaSlider && mediaSlider.getBoundingClientRect().height <= availableHeight;

		if ( entryContent && shouldPin ) {
			entryPinTrigger = ScrollTrigger.create( {
				trigger: entryContent,
				start: () => `top top+=${ getMastheadOffset() }`,
				end: () => `+=${ window.innerHeight }`,
				pin: true,
				pinSpacing: true,
				anticipatePin: true,
			} );
		}

		// Fade out intro layout as .entry-content comes into view
		if ( introLayout && entryContent ) {
			gsap.set( introLayout, { opacity: 1 } );

			fadeAnimation = gsap.to( introLayout, {
				opacity: 0,
				ease: 'none',
				scrollTrigger: {
					trigger: document.documentElement,
					start: 'top top',
					end: () => `+=${ Math.round( window.innerHeight * 1 ) }`,
					scrub: true,
				},
			} );
		}

		ScrollTrigger.refresh();

		// Signal to other blocks (e.g. scroll-slider) that the pin
		// spacer has been inserted and all scroll positions are stable
		document.dispatchEvent( new CustomEvent( 'poh:scroll-setup-done' ) );
	}

	/**
	 * Cleanup scroll effects
	 */
	function cleanupEffects() {
		if ( pinTrigger ) {
			pinTrigger.kill();
			pinTrigger = null;
		}

		if ( entryPinTrigger ) {
			entryPinTrigger.kill();
			entryPinTrigger = null;
		}

		if ( fadeAnimation ) {
			fadeAnimation.kill();
			fadeAnimation = null;
		}

		// Reset intro layout opacity if it exists
		if ( introLayout ) {
			gsap.set( introLayout, { opacity: 1 } );
		}

		ScrollTrigger.refresh();
	}

	/**
	 * Handle media query changes
	 * Debounced to prevent rapid toggling during resize
	 */
	const handleMediaChange = debounce( ( e ) => {
		if ( e.matches ) {
			// Mobile: cleanup effects
			cleanupEffects();
		} else {
			// Desktop: setup effects
			setupEffects();
		}
	}, 100 );

	// Init setup based on current viewport size
	if ( ! mobileQuery.matches ) {
		setupEffects();
	}

	// Listen for viewport changes
	mobileQuery.addEventListener( 'change', handleMediaChange );
}

/**
 * Init intro animations for hero elements
 * Animates .hero__graphic and .hero__content with staggered motion
 */
function initIntroAnimations( node, gsap ) {
	const graphic = node.querySelector( '.hero__graphic' );
	const content = node.querySelector( '.hero__content' );

	if ( ! graphic || ! content ) {
		return;
	}

	// Initial state (opacity and transform) is set in CSS to prevent FOUC
	// GSAP will read these values and animate from them

	// Create timeline
	const tl = gsap.timeline( {
		defaults: {
			delay: 0.2,
			duration: 0.8,
			ease: 'power2.out',
		},
	} );

	tl.to( graphic, { opacity: 1 } );
	tl.to( content, { opacity: 1, y: 0 }, '<0.25' );
}

/**
 * Init mouse-based parallax effect for decorative elements
 */
function initParallaxEffects( node, gsap ) {
	const decorA = node.querySelector( '.hero__graphic-decor-a' );
	const decorB = node.querySelector( '.hero__graphic-decor-b' );

	if ( ! decorA || ! decorB ) {
		return;
	}

	// Use quickTo for performant mouse tracking
	const moveA = gsap.quickTo( decorA, 'x', { duration: 0.6, ease: 'power1.out' } );
	const moveAY = gsap.quickTo( decorA, 'y', { duration: 0.6, ease: 'power1.out' } );
	const moveB = gsap.quickTo( decorB, 'x', { duration: 0.8, ease: 'power1.out' } );
	const moveBY = gsap.quickTo( decorB, 'y', { duration: 0.8, ease: 'power1.out' } );

	// Parallax intensity (adjust these values to control movement amount)
	const intensityA = 30; // pixels
	const intensityB = 50; // pixels

	const handleMouseMove = ( e ) => {
		// Calculate mouse position as percentage of viewport (-1 to 1)
		const x = ( e.clientX / window.innerWidth - 0.5 ) * 2;
		const y = ( e.clientY / window.innerHeight - 0.5 ) * 2;

		// Element A: moves with mouse, emphasizes X axis
		moveA( x * intensityA );
		moveAY( y * intensityA * 0.7 );

		// Element B: moves opposite direction, emphasizes Y axis
		moveB( -x * intensityB * 0.8 );
		moveBY( -y * intensityB );
	};

	const handleMouseLeave = () => {
		moveA( 0 );
		moveAY( 0 );
		moveB( 0 );
		moveBY( 0 );
	};

	node.addEventListener( 'mousemove', handleMouseMove );
	node.addEventListener( 'mouseleave', handleMouseLeave );
}

/**
 * Randomly swap the hero image from the gallery data attribute
 */
function initRandomImage( node ) {
	const galleryData = node.getAttribute( 'data-gallery' );
	if ( ! galleryData ) {
		return;
	}

	try {
		const images = JSON.parse( galleryData );
		if ( ! images.length ) {
			return;
		}

		const img = node.querySelector( '.hero__graphic-main' );
		if ( ! img ) {
			return;
		}

		const random = images[ Math.floor( Math.random() * images.length ) ];
		if ( ! random.src ) {
			return;
		}
		img.src = random.src;
		img.alt = random.alt || '';
	} catch ( e ) {
		// Fail silently — default image stays visible
	}
}

/**
 * Main init function
 */
function init( node ) {
	if ( ! ( node instanceof HTMLElement ) ) {
		return;
	}

	// Swap in a random gallery image before anything else
	initRandomImage( node );

	// Validate GSAP availability
	const { gsap } = window;
	if ( ! gsap ) {
		console.warn( 'GSAP not available. Make sure gsap and gsap-scroll-trigger are enqueued.' );
		return;
	}

	// ScrollTrigger registers itself with GSAP when loaded
	const ScrollTrigger = gsap.ScrollTrigger || window.ScrollTrigger;
	if ( ! ScrollTrigger ) {
		console.warn( 'ScrollTrigger not available. Make sure gsap-scroll-trigger is enqueued after gsap.' );
		return;
	}

	// Run intro animations first
	initIntroAnimations( node, gsap );

	// Then initialize scroll and parallax effects
	initScrollEffects( node, gsap, ScrollTrigger );
	initParallaxEffects( node, gsap );
}

/**
 * Init hero block when DOM is ready
 * Deferred scripts execute after DOMContentLoaded
 */
const initHero = () => {
	const heroElement = document.querySelector( '.hero' );
	if ( heroElement ) {
		init( heroElement );
	}
};

if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', initHero );
} else {
	initHero();
}
